import _ from 'lodash';

import { productDisplayName } from './content-config/adapters';

export const isContentPortalProductsEnabled = () =>
  _.get(window, 'Grab.config.ENABLE_CONTENT_PORTAL_PRODUCTS', false) === true;

const sameVertical = (productVertical: string, selectedVertical: string) =>
  String(productVertical || '').toLowerCase() === String(selectedVertical || '').toLowerCase();

const safeProductDisplayName = (
  product: any,
  selectedLanguage: string = 'en',
) => {
  const displayName = productDisplayName(product || {}, selectedLanguage || 'en');
  return displayName === _.get(product, 'id') ? '' : displayName || '';
};

export const getContentPortalProductOptions = (
  products: any[] = [],
  selectedVertical: string = '',
  selectedLanguage: string = 'en',
) =>
  products
    .filter((product) => !selectedVertical || sameVertical(product.vertical, selectedVertical))
    .map((product) => ({
      value: product.id,
      label: safeProductDisplayName(product, selectedLanguage || 'en'),
    }));

export const getContentPortalAttributeOptions = (
  selectedProduct: any,
  attributes: any[] = [],
) => {
  const whitelistedAttributes = _.get(selectedProduct, 'whitelist.attrs', []);
  const selectedProductAttributes = whitelistedAttributes.length > 0
    ? whitelistedAttributes
    : _.get(selectedProduct, 'attributes', []);
  const whitelistedAttributeSet = new Set(selectedProductAttributes);

  if (attributes.length > 0) {
    return attributes
      .filter((attribute) => attribute && attribute.key)
      .map((attribute) => ({
        value: attribute.key,
        label: attribute.key,
        isWhitelisted: whitelistedAttributeSet.has(attribute.key),
        definition: _.get(attribute, 'definition', ''),
      }));
  }

  return selectedProductAttributes.map((key) => ({
    value: key,
    label: key,
    isWhitelisted: true,
    definition: '',
  }));
};

const localizedValue = (
  valuesByLanguage: Record<string, string> | undefined = {},
  selectedLanguage: string = 'en',
) => (
  valuesByLanguage[selectedLanguage]
  || valuesByLanguage.en
  || Object.values(valuesByLanguage).find(Boolean)
  || ''
);

export const getContentPortalProductName = (
  product: any,
  selectedLanguage: string = 'en',
) => safeProductDisplayName(product, selectedLanguage);

const trimmedString = (value: any) => String(value || '').trim();

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const nonUuidFallback = (value: any, productId: any = '') => {
  const fallback = trimmedString(value);
  if (!fallback || fallback === trimmedString(productId) || UUID_PATTERN.test(fallback)) {
    return '';
  }

  return fallback;
};

const getContentPortalPayloadProductName = (
  product: any,
  selectedLanguage: string = 'en',
) => {
  const byLang = _.get(product, 'byLang', {});
  const lang = selectedLanguage || 'en';

  return (
    trimmedString(_.get(byLang, [lang, 'name']))
    || trimmedString(_.get(byLang, ['en', 'name']))
    || Object.keys(byLang)
      .sort()
      .map((language) => trimmedString(_.get(byLang, [language, 'name'])))
      .find(Boolean)
    || nonUuidFallback(_.get(product, 'name'), _.get(product, 'id'))
    || nonUuidFallback(_.get(product, 'key'), _.get(product, 'id'))
    || ''
  );
};

export const buildContentPortalProductOfferingConfig = (
  product: any,
  selectedLanguage: string = 'en',
) => {
  const byLang = _.get(product, 'byLang', {});
  const productOverview: Record<string, string> = {};
  const reasonToUse: Record<string, string> = {};
  const callToAction: Record<string, string> = {};

  Object.keys(byLang).forEach((lang) => {
    productOverview[lang] = byLang[lang].overview || '';
    reasonToUse[lang] = byLang[lang].reason || '';
    callToAction[lang] = byLang[lang].cta || '';
  });

  return {
    key: _.get(product, 'id') || _.get(product, 'key') || '',
    displayName: getContentPortalPayloadProductName(product, selectedLanguage),
    productOverview,
    reasonToUse,
    callToAction,
    cvp: { exclusions: [], dependencies: [] },
    personalAttributes: null,
  };
};

const normalizedKey = (value: any) => String(value || '').trim().toLowerCase();

const cvpKey = (cvp: any) => {
  if (typeof cvp === 'string') return cvp;
  return _.get(cvp, 'meta.key') || _.get(cvp, 'key') || _.get(cvp, 'code') || '';
};

export const deriveExcludedCvps = (
  allCvps: any[] = [],
  includedCvps: any[] = [],
) => {
  const includedSet = new Set(includedCvps.map(normalizedKey));

  return allCvps
    .map(cvpKey)
    .filter(Boolean)
    .filter((key) => !includedSet.has(normalizedKey(key)));
};

export const buildMystiqueV1OverridePayload = (
  values: any,
  selectedProduct: any,
  offeringConfig: any,
  allCvps: any[] = [],
) => {
  const lang = values.language || 'en';
  const productOffering = getContentPortalPayloadProductName(selectedProduct, lang);
  const config = offeringConfig || buildContentPortalProductOfferingConfig(selectedProduct, lang);
  const productName = _.get(config, 'hasDisplayNameOverride')
    ? trimmedString(_.get(config, 'displayName')) || productOffering
    : productOffering;
  const selectedProductCvps = _.get(selectedProduct, 'whitelist.cvps', _.get(selectedProduct, 'cvps', []));
  const derivedExcludedCvps = deriveExcludedCvps(allCvps, selectedProductCvps);
  const payload: Record<string, any> = {
    channel: 'push',
    countryCode: values.country,
    language: lang,
    vertical: values.vertical,
    productOffering,
    segmentType:
      values.attributes && values.attributes.length > 0
        ? 'attributepairing'
        : null,
    excludedCVPs: derivedExcludedCvps,
    customProductConfig: {
      productName,
      productOverview: localizedValue(config.productOverview, lang),
      reasonToUse: localizedValue(config.reasonToUse, lang),
      callToAction: localizedValue(config.callToAction, lang),
      cvp: { exclusions: derivedExcludedCvps },
    },
  };

  if (values.attributes && values.attributes.length > 0) {
    payload.segmentName = values.attributes.join(', ');
  }

  if (values.dateRange && values.dateRange[0]) {
    payload.startDate = values.dateRange[0].format('YYYY-MM-DD');
  }
  if (values.dateRange && values.dateRange[1]) {
    payload.endDate = values.dateRange[1].format('YYYY-MM-DD');
  }

  return payload;
};

export const parseMystiqueContentItems = (result: any = {}) => {
  const items: any[] = [];

  if (result && result.payloads && Array.isArray(result.payloads)) {
    result.payloads.forEach((p) => {
      if (p && !p.error) {
        items.push({
          title: p.title || p.headline || 'Untitled',
          message: p.body || p.message || '',
          cvp: p.cvp || '',
          score: (p.review && p.review.score) || p.score || 0,
          reviewResult: p.review && p.review.result,
          reviewReason: p.review && p.review.reason,
        });
      }
    });
    return items;
  }

  if (result && result.content_by_cvp) {
    Object.entries(result.content_by_cvp).forEach(([cvpName, cvpData]) => {
      const data: any = cvpData;
      if (data && !data.error) {
        items.push({
          title: data.title || 'Untitled',
          message: data.body || '',
          cvp: cvpName,
          score: (data.review && data.review.score) || data.score || 0,
          reviewResult: data.review && data.review.result,
          reviewReason: data.review && data.review.reason,
        });
      }
    });
  }

  return items;
};
